import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { MusicNote, SignOut, User, List } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';

export const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const dashboardLink = user?.user_type === 'musician' ? '/dashboard' : '/organizer';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-40 ${transparent ? '' : 'glass'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <div className="w-10 h-10 rounded-lg bg-[#CCFF00] flex items-center justify-center group-hover:bg-[#E5FF00] transition-colors">
              <MusicNote size={24} weight="fill" className="text-black" />
            </div>
            <span className="font-bold text-xl tracking-tight hidden sm:block">BeatPromoter</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/discover" 
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="nav-discover"
            >
              Scopri
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 text-white hover:text-[#CCFF00] transition-colors" data-testid="nav-user-menu">
                    <div className="w-8 h-8 rounded-full bg-[#1A1A1A] flex items-center justify-center">
                      <User size={18} weight="fill" />
                    </div>
                    <span className="font-medium">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#111] border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to={dashboardLink} className="cursor-pointer" data-testid="nav-dashboard-link">
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-400" data-testid="nav-logout">
                    <SignOut size={16} className="mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/auth">
                  <Button variant="ghost" className="text-white hover:text-[#CCFF00]" data-testid="nav-login">
                    Accedi
                  </Button>
                </Link>
                <Link to="/auth?mode=register">
                  <Button className="bg-[#CCFF00] text-black hover:bg-[#E5FF00] rounded-full px-6" data-testid="nav-register">
                    Registrati
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="nav-mobile-menu"
          >
            <List size={24} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#111] border-t border-white/10">
          <div className="px-4 py-4 space-y-4">
            <Link 
              to="/discover" 
              className="block text-[#A1A1AA] hover:text-white"
              onClick={() => setMobileMenuOpen(false)}
            >
              Scopri
            </Link>
            {user ? (
              <>
                <Link 
                  to={dashboardLink} 
                  className="block text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button 
                  onClick={handleLogout}
                  className="block text-red-400"
                >
                  Esci
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/auth" 
                  className="block text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Accedi
                </Link>
                <Link 
                  to="/auth?mode=register"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button className="w-full bg-[#CCFF00] text-black">
                    Registrati
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
